import React, { useState, useRef, useEffect } from 'react';
import { Menu, X, Heart, MessageSquare, Bell, Search, Map, Info, Car, Users, FileText, Globe, RefreshCw, Home, Compass, ChevronDown, ChevronUp, Leaf, Coffee, Building, Download, Moon } from 'lucide-react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import Logo from './Logo';
import LanguageTranslator from './LanguageTranslator';
import ThemeToggle from './ThemeToggle';
import CurrencySelector from './CurrencySelector';
import { changeLanguage } from '../i18n';

const Navbar = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isLanguageModalOpen, setIsLanguageModalOpen] = useState(false);
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [isAdventuresOpen, setIsAdventuresOpen] = useState(false);
  const [isAboutOpen, setIsAboutOpen] = useState(false);
  const [isMobileAdventuresOpen, setIsMobileAdventuresOpen] = useState(false);
  const [isMobileAboutOpen, setIsMobileAboutOpen] = useState(false);
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const adventuresRef = useRef<HTMLDivElement>(null);
  const aboutRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const searchContainerRef = useRef<HTMLDivElement>(null);

  // Get current language info
  const getCurrentLanguage = () => {
    const langCode = i18n.language.split('-')[0]; // Handle cases like 'en-US'
    const language = languages.find(lang => lang.code === langCode);
    return language || languages[0]; // Default to English if not found
  };

  const languages = [
    { code: 'en', name: 'English', flag: '🇬🇧' },
    { code: 'fr', name: 'Français', flag: '🇫🇷' },
    { code: 'sw', name: 'Kiswahili', flag: '🇹🇿' }
  ];

  const adventuresDropdown = [
    { path: '/uganda-trips', label: t('adventures.all'), icon: Map },
    { path: '/safari-packages', label: t('adventures.safariPackages'), icon: Car },
    { path: '/uganda-city-tours', label: t('adventures.cityTours'), icon: Building },
    { path: '/cultural-tours', label: t('adventures.culturalTours'), icon: Coffee },
    { path: '/self-drive-safaris', label: t('adventures.selfDrive'), icon: Car },
    { path: '/all-experiences', label: t('adventures.allExperiences'), icon: Globe },
    { path: '/gorilla-trekking', label: t('adventures.gorillaTrekking'), icon: Leaf },
    { path: '/chimpanzee-tracking', label: t('adventures.chimpanzeeTracking'), icon: Leaf },
    { path: '/customize-adventure', label: t('adventures.customizeSafari'), icon: Map },
    { path: '/Uganda_Nightlife_Ultimate_Guide', label: t('adventures.nightlifeGuide'), icon: Moon }
  ];

  const aboutDropdown = [
    { path: '/about-us', label: t('nav.aboutUs'), icon: Info },
    { path: '/travel-guides', label: 'Our Guides', icon: Users },
    { path: '/resources', label: 'Free Resources', icon: Download },
    { path: '/terms-of-service', label: 'Terms & Conditions', icon: FileText }
  ];

  const mainNavItems = [
    { path: '/', icon: Home, label: t('nav.home') },
    { path: '/uganda', icon: Map, label: t('nav.uganda') },
    { path: '/blog', icon: FileText, label: t('nav.blog') }
  ];

  useEffect(() => {
    if (isMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'auto';
    }
    return () => {
      document.body.style.overflow = 'auto';
    };
  }, [isMenuOpen]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (adventuresRef.current && !adventuresRef.current.contains(event.target as Node)) {
        setIsAdventuresOpen(false);
      }
      if (aboutRef.current && !aboutRef.current.contains(event.target as Node)) {
        setIsAboutOpen(false);
      }
      if (searchContainerRef.current && !searchContainerRef.current.contains(event.target as Node)) {
        setIsSearchOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleRefresh = () => {
    setIsRefreshing(true);
    setTimeout(() => {
      window.location.reload();
    }, 500);
  };

  const handleSearchSelect = (result: any) => {
    setSearchQuery(result.title);
    setIsSearchOpen(false);
    navigate(result.path);
  };

  const handleLanguageChange = (langCode: string) => {
    changeLanguage(langCode);
  };

  const handleSearchFocus = () => {
    setIsSearchOpen(true);
    if (searchInputRef.current) {
      searchInputRef.current.focus();
    }
  };

  const handleSearchSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      // Navigate to search results page or handle search
      if (searchQuery.toLowerCase().includes('gorilla')) {
        navigate('/gorilla-trekking');
      } else if (searchQuery.toLowerCase().includes('safari')) {
        navigate('/safari-packages');
      } else if (searchQuery.toLowerCase().includes('city') || searchQuery.toLowerCase().includes('kampala')) {
        navigate('/uganda-city-tours');
      } else if (searchQuery.toLowerCase().includes('night') || searchQuery.toLowerCase().includes('club')) {
        navigate('/Uganda_Nightlife_Ultimate_Guide');
      } else {
        navigate('/all-experiences');
      }
      setIsSearchOpen(false);
    }
  };

  const menuVariants = {
    hidden: { 
      opacity: 0,
      x: '-100%',
    },
    visible: {
      opacity: 1,
      x: 0,
      transition: {
        type: 'tween',
        duration: 0.3,
        ease: 'easeInOut'
      }
    },
    exit: {
      opacity: 0,
      x: '-100%',
      transition: {
        type: 'tween',
        duration: 0.3,
        ease: 'easeInOut'
      }
    }
  };

  const dropdownVariants = {
    hidden: { 
      opacity: 0,
      y: -5,
      scale: 0.95
    },
    visible: {
      opacity: 1,
      y: 0,
      scale: 1,
      transition: {
        type: 'spring',
        stiffness: 300,
        damping: 20
      }
    },
    exit: {
      opacity: 0,
      y: -5,
      scale: 0.95,
      transition: {
        duration: 0.15
      }
    }
  };

  const mobileDropdownVariants = {
    hidden: {
      height: 0,
      opacity: 0
    },
    visible: {
      height: 'auto',
      opacity: 1,
      transition: {
        height: {
          type: 'spring',
          stiffness: 300,
          damping: 30
        },
        opacity: {
          duration: 0.2
        }
      }
    },
    exit: {
      height: 0,
      opacity: 0,
      transition: {
        height: {
          duration: 0.2
        },
        opacity: {
          duration: 0.1
        }
      }
    }
  };

  const searchVariants = {
    hidden: { 
      opacity: 0,
      width: 0,
      scale: 0.9
    },
    visible: {
      opacity: 1,
      width: "300px",
      scale: 1,
      transition: {
        type: 'spring',
        stiffness: 300,
        damping: 25
      }
    },
    exit: {
      opacity: 0,
      width: 0,
      scale: 0.9,
      transition: {
        duration: 0.2
      }
    }
  };

  const currentLanguage = getCurrentLanguage();

  return (
    <nav className="bg-white dark:bg-gray-900 border-b border-gray-200 dark:border-gray-800 sticky top-0 z-50">
      <div className="max-w-7xl mx-auto px-4">
        <div className="flex justify-between h-16">
          <div className="flex items-center">
            <button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="md:hidden inline-flex items-center justify-center p-2 rounded-md text-gray-700 dark:text-gray-300 hover:text-ta-green focus:outline-none"
            >
              {isMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </button>
            <Link to="/" className="flex items-center">
              <Logo />
            </Link>
          </div>

          {/* Desktop Navigation */}
          <div className="hidden md:flex md:items-center md:space-x-1 lg:space-x-2">
            <Link
              to="/"
              className={`flex items-center px-2 py-2 text-xs font-medium rounded-lg transition-colors ${
                location.pathname === '/'
                  ? 'text-ta-green bg-ta-green/5'
                  : 'text-gray-700 dark:text-gray-300 hover:text-ta-green hover:bg-gray-50 dark:hover:bg-gray-800'
              }`}
            >
              <Home className="h-3.5 w-3.5 mr-1" />
              {t('nav.home')}
            </Link>

            {/* Adventures Dropdown */}
            <div ref={adventuresRef} className="relative">
              <button
                onClick={() => setIsAdventuresOpen(!isAdventuresOpen)}
                className="flex items-center px-2 py-2 text-xs font-medium rounded-lg transition-colors text-gray-700 dark:text-gray-300 hover:text-ta-green hover:bg-gray-50 dark:hover:bg-gray-800"
              >
                <Compass className="h-3.5 w-3.5 mr-1" />
                {t('nav.adventures')}
                {isAdventuresOpen ? (
                  <ChevronUp className="ml-1 h-3.5 w-3.5" />
                ) : (
                  <ChevronDown className="ml-1 h-3.5 w-3.5" />
                )}
              </button>

              <AnimatePresence>
                {isAdventuresOpen && (
                  <motion.div
                    initial="hidden"
                    animate="visible"
                    exit="exit"
                    variants={dropdownVariants}
                    className="absolute top-full left-0 mt-1 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 py-2 min-w-[600px]"
                  >
                    <div className="grid grid-cols-3 gap-2 p-2">
                      {adventuresDropdown.map((item) => (
                        <Link
                          key={item.path}
                          to={item.path}
                          className="flex items-center px-3 py-2 text-xs text-gray-700 dark:text-gray-300 hover:bg-ta-green/5 hover:text-ta-green rounded-lg transition-colors group"
                          onClick={() => setIsAdventuresOpen(false)}
                        >
                          <item.icon className="h-3.5 w-3.5 mr-2 text-gray-400 group-hover:text-ta-green transition-colors" />
                          <span>{item.label}</span>
                        </Link>
                      ))}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            {/* About TravelCord Dropdown */}
            <div ref={aboutRef} className="relative">
              <button
                onClick={() => setIsAboutOpen(!isAboutOpen)}
                className="flex items-center px-2 py-2 text-xs font-medium rounded-lg transition-colors text-gray-700 dark:text-gray-300 hover:text-ta-green hover:bg-gray-50 dark:hover:bg-gray-800"
              >
                <Building className="h-3.5 w-3.5 mr-1" />
                {t('nav.aboutUs')}
                {isAboutOpen ? (
                  <ChevronUp className="ml-1 h-3.5 w-3.5" />
                ) : (
                  <ChevronDown className="ml-1 h-3.5 w-3.5" />
                )}
              </button>

              <AnimatePresence>
                {isAboutOpen && (
                  <motion.div
                    initial="hidden"
                    animate="visible"
                    exit="exit"
                    variants={dropdownVariants}
                    className="absolute top-full left-0 mt-1 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 py-2"
                    style={{ width: '400px' }}
                  >
                    <div className="grid grid-cols-2 gap-1 p-1">
                      {aboutDropdown.map((item) => (
                        <Link
                          key={item.path}
                          to={item.path}
                          className="flex items-center px-3 py-2 text-xs text-gray-700 dark:text-gray-300 hover:bg-ta-green/5 hover:text-ta-green rounded-lg transition-colors group"
                          onClick={() => setIsAboutOpen(false)}
                        >
                          <item.icon className="h-3.5 w-3.5 mr-2 text-gray-400 group-hover:text-ta-green transition-colors" />
                          <span>{item.label}</span>
                        </Link>
                      ))}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            {mainNavItems.slice(1).map((item) => (
              <Link
                key={item.path}
                to={item.path}
                className={`flex items-center px-2 py-2 text-xs font-medium rounded-lg transition-colors ${
                  location.pathname === item.path
                    ? 'text-ta-green bg-ta-green/5'
                    : 'text-gray-700 dark:text-gray-300 hover:text-ta-green hover:bg-gray-50 dark:hover:bg-gray-800'
                }`}
              >
                <item.icon className="h-3.5 w-3.5 mr-1" />
                {item.label}
              </Link>
            ))}
          </div>

          {/* Right Side Icons */}
          <div className="flex items-center space-x-1 lg:space-x-2">
            <div className="relative" ref={searchContainerRef}>
              <form onSubmit={handleSearchSubmit}>
                <AnimatePresence>
                  {isSearchOpen ? (
                    <motion.div
                      initial="hidden"
                      animate="visible"
                      exit="exit"
                      variants={searchVariants}
                      className="absolute right-0 top-1/2 transform -translate-y-1/2 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 overflow-hidden z-50"
                    >
                      <div className="relative">
                        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                        <input
                          ref={searchInputRef}
                          type="text"
                          placeholder={t('common.search')}
                          value={searchQuery}
                          onChange={(e) => setSearchQuery(e.target.value)}
                          className="w-full pl-10 pr-8 py-2 bg-gray-50 dark:bg-gray-700 border-none text-gray-700 dark:text-gray-200 focus:outline-none focus:ring-2 focus:ring-ta-green text-sm"
                        />
                        {searchQuery && (
                          <button
                            type="button"
                            className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                            onClick={() => setSearchQuery('')}
                          >
                            <X className="h-4 w-4" />
                          </button>
                        )}
                      </div>
                      
                      {searchQuery && (
                        <div className="max-h-60 overflow-y-auto border-t border-gray-100 dark:border-gray-700">
                          <div className="py-1 px-2 text-xs text-gray-500 dark:text-gray-400">
                            {t('common.search')}
                          </div>
                          <div className="space-y-1 p-1">
                            {['Gorilla Trekking', 'Safari Packages', 'Kampala Tours', 'Nightlife Guide'].filter(item => 
                              item.toLowerCase().includes(searchQuery.toLowerCase())
                            ).map((item, index) => (
                              <div 
                                key={index}
                                className="px-3 py-2 rounded-md hover:bg-gray-100 dark:hover:bg-gray-700 cursor-pointer text-sm flex items-center"
                                onClick={() => {
                                  setSearchQuery(item);
                                  // Navigate based on search term
                                  if (item === 'Gorilla Trekking') navigate('/gorilla-trekking');
                                  else if (item === 'Safari Packages') navigate('/safari-packages');
                                  else if (item === 'Kampala Tours') navigate('/uganda-city-tours');
                                  else if (item === 'Nightlife Guide') navigate('/Uganda_Nightlife_Ultimate_Guide');
                                  setIsSearchOpen(false);
                                }}
                              >
                                <Search className="h-3.5 w-3.5 text-gray-400 mr-2" />
                                {item}
                              </div>
                            ))}
                            {!['Gorilla Trekking', 'Safari Packages', 'Kampala Tours', 'Nightlife Guide'].some(item => 
                              item.toLowerCase().includes(searchQuery.toLowerCase())
                            ) && (
                              <div className="px-3 py-2 text-sm text-gray-500">
                                {t('pages.allExperiences.noResults')} "{searchQuery}"
                              </div>
                            )}
                          </div>
                        </div>
                      )}
                    </motion.div>
                  ) : (
                    <button
                      type="button"
                      onClick={handleSearchFocus}
                      className="p-2 rounded-lg text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
                      aria-label="Search"
                    >
                      <Search className="h-5 w-5" />
                    </button>
                  )}
                </AnimatePresence>
              </form>
            </div>
            
            <button 
              onClick={handleRefresh}
              className="p-2 rounded-lg text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
            >
              <RefreshCw className={`h-5 w-5 ${isRefreshing ? 'animate-spin text-ta-green' : ''}`} />
            </button>
            
            <ThemeToggle />
            
            {/* Currency Selector */}
            <CurrencySelector />
            
            <button 
              onClick={() => setIsLanguageModalOpen(true)}
              className="flex items-center p-2 rounded-lg text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
              data-translator-button
            >
              <span className="mr-1 text-base">{currentLanguage.flag}</span>
              <span className="hidden lg:inline text-xs mr-1">{currentLanguage.name}</span>
              <ChevronDown className="h-3.5 w-3.5" />
            </button>
            
            <button className="md:flex hidden p-2 rounded-lg text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors">
              <Bell className="h-5 w-5" />
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu */}
      <AnimatePresence>
        {isMenuOpen && (
          <motion.div
            initial="hidden"
            animate="visible"
            exit="exit"
            variants={menuVariants}
            className="fixed inset-0 z-40 bg-white dark:bg-gray-900 md:hidden"
          >
            <div className="flex flex-col h-full">
              {/* Mobile Menu Header */}
              <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-800">
                <Logo />
                <button
                  onClick={() => setIsMenuOpen(false)}
                  className="p-2 rounded-full hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
                >
                  <X className="h-6 w-6 text-gray-600 dark:text-gray-400" />
                </button>
              </div>

              {/* Mobile Search */}
              <div className="p-4 border-b border-gray-200 dark:border-gray-800">
                <form onSubmit={handleSearchSubmit}>
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                    <input
                      type="text"
                      placeholder={t('common.search')}
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="w-full pl-10 pr-4 py-2 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-gray-700 dark:text-gray-200 focus:outline-none focus:ring-2 focus:ring-ta-green"
                    />
                    {searchQuery && (
                      <button
                        type="button"
                        className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                        onClick={() => setSearchQuery('')}
                      >
                        <X className="h-4 w-4" />
                      </button>
                    )}
                  </div>
                  
                  {searchQuery && (
                    <div className="mt-2 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
                      <div className="py-1 px-2 text-xs text-gray-500 dark:text-gray-400">
                        {t('common.search')}
                      </div>
                      <div className="space-y-1 p-1">
                        {['Gorilla Trekking', 'Safari Packages', 'Kampala Tours', 'Nightlife Guide'].filter(item => 
                          item.toLowerCase().includes(searchQuery.toLowerCase())
                        ).map((item, index) => (
                          <div 
                            key={index}
                            className="px-3 py-2 rounded-md hover:bg-gray-100 dark:hover:bg-gray-700 cursor-pointer text-sm flex items-center"
                            onClick={() => {
                              setSearchQuery(item);
                              // Navigate based on search term
                              if (item === 'Gorilla Trekking') navigate('/gorilla-trekking');
                              else if (item === 'Safari Packages') navigate('/safari-packages');
                              else if (item === 'Kampala Tours') navigate('/uganda-city-tours');
                              else if (item === 'Nightlife Guide') navigate('/Uganda_Nightlife_Ultimate_Guide');
                              setIsMenuOpen(false);
                            }}
                          >
                            <Search className="h-3.5 w-3.5 text-gray-400 mr-2" />
                            {item}
                          </div>
                        ))}
                        {!['Gorilla Trekking', 'Safari Packages', 'Kampala Tours', 'Nightlife Guide'].some(item => 
                          item.toLowerCase().includes(searchQuery.toLowerCase())
                        ) && (
                          <div className="px-3 py-2 text-sm text-gray-500">
                            {t('pages.allExperiences.noResults')} "{searchQuery}"
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </form>
              </div>

              {/* Mobile Menu Content */}
              <div className="flex-1 overflow-y-auto py-4 px-4">
                <div className="space-y-1">
                  <Link
                    to="/"
                    className={`flex items-center px-3 py-3 rounded-lg text-sm font-medium ${
                      location.pathname === '/'
                        ? 'text-ta-green bg-ta-green/5'
                        : 'text-gray-700 dark:text-gray-300 hover:text-ta-green hover:bg-gray-50 dark:hover:bg-gray-800'
                    } transition-all duration-200`}
                    onClick={() => setIsMenuOpen(false)}
                  >
                    <Home className="h-4 w-4 mr-3" />
                    {t('nav.home')}
                  </Link>

                  {/* Mobile Adventures Menu */}
                  <div>
                    <button
                      onClick={() => setIsMobileAdventuresOpen(!isMobileAdventuresOpen)}
                      className="w-full flex items-center justify-between px-3 py-3 rounded-lg text-sm font-medium text-gray-700 dark:text-gray-300 hover:text-ta-green hover:bg-gray-50 dark:hover:bg-gray-800 transition-all duration-200"
                    >
                      <div className="flex items-center">
                        <Compass className="h-4 w-4 mr-3" />
                        {t('nav.adventures')}
                      </div>
                      {isMobileAdventuresOpen ? (
                        <ChevronUp className="h-4 w-4" />
                      ) : (
                        <ChevronDown className="h-4 w-4" />
                      )}
                    </button>

                    <AnimatePresence>
                      {isMobileAdventuresOpen && (
                        <motion.div
                          initial="hidden"
                          animate="visible"
                          exit="exit"
                          variants={mobileDropdownVariants}
                          className="pl-10 space-y-1 overflow-hidden"
                        >
                          {adventuresDropdown.map((item) => (
                            <Link
                              key={item.path}
                              to={item.path}
                              className="flex items-center px-3 py-2 text-sm text-gray-700 dark:text-gray-300 hover:text-ta-green hover:bg-gray-50 dark:hover:bg-gray-800 rounded-lg transition-colors group"
                              onClick={() => setIsMenuOpen(false)}
                            >
                              <item.icon className="h-4 w-4 mr-2 text-gray-400 group-hover:text-ta-green transition-colors" />
                              <span>{item.label}</span>
                            </Link>
                          ))}
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>

                  {/* Mobile About Menu */}
                  <div>
                    <button
                      onClick={() => setIsMobileAboutOpen(!isMobileAboutOpen)}
                      className="w-full flex items-center justify-between px-3 py-3 rounded-lg text-sm font-medium text-gray-700 dark:text-gray-300 hover:text-ta-green hover:bg-gray-50 dark:hover:bg-gray-800 transition-all duration-200"
                    >
                      <div className="flex items-center">
                        <Building className="h-4 w-4 mr-3" />
                        {t('nav.aboutUs')}
                      </div>
                      {isMobileAboutOpen ? (
                        <ChevronUp className="h-4 w-4" />
                      ) : (
                        <ChevronDown className="h-4 w-4" />
                      )}
                    </button>

                    <AnimatePresence>
                      {isMobileAboutOpen && (
                        <motion.div
                          initial="hidden"
                          animate="visible"
                          exit="exit"
                          variants={mobileDropdownVariants}
                          className="pl-10 space-y-1 overflow-hidden"
                        >
                          {aboutDropdown.map((item) => (
                            <Link
                              key={item.path}
                              to={item.path}
                              className="flex items-center px-3 py-2 text-sm text-gray-700 dark:text-gray-300 hover:text-ta-green hover:bg-gray-50 dark:hover:bg-gray-800 rounded-lg transition-colors group"
                              onClick={() => setIsMenuOpen(false)}
                            >
                              <item.icon className="h-4 w-4 mr-2 text-gray-400 group-hover:text-ta-green transition-colors" />
                              <span>{item.label}</span>
                            </Link>
                          ))}
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>

                  {mainNavItems.slice(1).map((item) => (
                    <Link
                      key={item.path}
                      to={item.path}
                      className={`flex items-center px-3 py-3 rounded-lg text-sm font-medium ${
                        location.pathname === item.path
                          ? 'text-ta-green bg-ta-green/5'
                          : 'text-gray-700 dark:text-gray-300 hover:text-ta-green hover:bg-gray-50 dark:hover:bg-gray-800'
                      } transition-all duration-200`}
                      onClick={() => setIsMenuOpen(false)}
                    >
                      <item.icon className="h-4 w-4 mr-3" />
                      {item.label}
                    </Link>
                  ))}
                </div>
              </div>

              {/* Mobile Menu Footer */}
              <div className="p-4 border-t border-gray-200 dark:border-gray-800">
                <div className="flex items-center justify-between">
                  <ThemeToggle />
                  <div className="flex items-center space-x-2">
                    {languages.map(lang => (
                      <button
                        key={lang.code}
                        onClick={() => handleLanguageChange(lang.code)}
                        className={`flex items-center justify-center w-10 h-10 rounded-full transition-colors ${
                          i18n.language === lang.code 
                            ? 'bg-ta-green text-white' 
                            : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300'
                        }`}
                      >
                        <span className="text-lg">{lang.flag}</span>
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <LanguageTranslator
        isOpen={isLanguageModalOpen}
        onClose={() => setIsLanguageModalOpen(false)}
      />
    </nav>
  );
};

export default Navbar;